import {
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Contact, MessageTitle, UserType } from 'src/app/enums/constants';
import { SaveEventAction } from 'src/app/enums/saveEventAction';
import { SaveEventData } from 'src/app/interfaces/saveEventData';
import { ContactService } from 'src/app/services/contact.service';
import { ErrorManagerService } from 'src/app/services/error-manager.service';
import {
  contactRelationships,
  spouseRelationships,
} from 'src/app/share/contactRelatioships';
import { contactTypes } from 'src/app/share/contactTypes';
import { ContactForm } from '../../../interfaces/ContactForm';
import { ContactPermissions } from '../../../interfaces/ContactPermissions';
import { ApiService } from '../../../services/api.service';
import { ContactAddressService } from '../../../services/contactAddress.service';
import { UserService } from '../../../services/user.service';
import { ConfirmDialogComponent } from '../..//confirm-dialog/confirm-dialog.component';
import { AddContactAddressComponent } from '../../contact-addresses/add/add.component';

@Component({
    selector: 'app-add',
    templateUrl: './add.component.html',
    styleUrls: ['./add.component.scss'],
    standalone: false
})
export class AddContactComponent implements OnInit, OnDestroy {
  onSaveEvent = new EventEmitter<SaveEventData>();
  readonly ContactEnum = Contact;
  address: any = null;
  isAddressNewlyAdded = false;
  model: any;
  editing: boolean;
  saving: boolean;
  addModal: boolean;
  dialogConfig: MatDialogConfig;
  hide: boolean;
  onlyaccesstype: boolean = false;
  loading: boolean = false;
  editPersonalSpouse: boolean = false;
  editWillExecutor: boolean = false;
  editTrustee: boolean = false;
  isInSignUp: boolean = false;
  relationshipOptions: any[] = [];
  typeOptions: typeof contactTypes = [];
  phoneChangeRequested: boolean = false;
  actsToDisplay = null;
  editBeneficiary: boolean = false;
  usertype = UserType.CONTACT;
  UserTypeEnum = UserType;

  bankAccounts: string[] = [];
  realEstates: string[] = [];
  loginAccounts: string[] = [];
  trust: string[] = [];
  insurances: string[] = [];
  investments: string[] = [];
  multimedias: string[] = [];
  additionalDocs: string[] = [];
  wills: string[] = [];
  taxAccountants: string[] = [];
  taxReturn: string[] = [];
  taxDocumentation: string[] = [];
  taxBasis: string[] = [];
  identification: string[] = [];
  retirementAccounts: any[] = [];
  vaultUserForAdmin = null;

  typeFieldValueChangeSubscription: Subscription = null;

  cardsPermissionsForModel: ContactPermissions = {
    bankAccounts: [],
    realEstates: [],
    loginAccounts: [],
    trust: [],
    additionalDocs: [],
    insurances: [],
    investments: [],
    multimedias: [],
    wills: [],
    retirementAccounts: [],
    taxAccountants: [],
    taxReturn: [],
    taxDocumentation: [],
    taxBasis: [],
    identification: [],
  };
  openedAsFormInput: boolean = false;

  form = new FormGroup<ContactForm>({
    first_name: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
    ]),
    last_name: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
    ]),
    middle_name: new FormControl(''),
    email: new FormControl('', [
      Validators.required,
      Validators.minLength(4),
      Validators.email,
    ]),
    phone: new FormControl('', [
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(10),
    ]),
    workPhone: new FormControl('', [
      Validators.minLength(10),
      Validators.maxLength(10),
    ]),
    relationship: new FormControl('', []),
    addressId: new FormControl(0, []),
    type: new FormControl(null, [Validators.required]),
    notifyOnDeath: new FormControl(false, []),
    allowAllItems: new FormControl(false, []),
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<AddContactComponent>,
    private apiService: ApiService,
    private contactAddressService: ContactAddressService,
    private matDialog: MatDialog,
    private userService: UserService,
    public errorManager: ErrorManagerService,
    private router: Router,
    private contactService: ContactService
  ) {
    this.usertype = this.userService.usertype;
    if (data) {
      this.vaultUserForAdmin = data.user ?? null;
      if (data.model) {
        this.model = data.model;
        if (data.model.address) {
          this.address = data.model.address;
        }

        if (this.usertype === UserType.ADMIN) {
          this.populateCardMinimumDataForAdmin();
        }

        const user =
          this.usertype === UserType.USER
            ? this.userService.user
            : this.usertype === UserType.CONTACT
            ? this.userService.user.openedVaultForUser
            : this.vaultUserForAdmin;

        let roles = this.contactService.getContactRoles(this.model, user);
        this.editPersonalSpouse = roles.includes(Contact.ACTOR_SPOUSE);
        this.editWillExecutor = roles.includes(Contact.ACTOR_EXECUTOR);
        this.editTrustee =
          roles.includes(Contact.ACTOR_PRIMARY_TRUSTEE) ||
          roles.includes(Contact.ACTOR_SECONDARY_TRUSTEE);
        this.editBeneficiary =
          roles.includes(Contact.ACTOR_PRIMARY_BENEFICIARY) ||
          roles.includes(Contact.ACTOR_SECONDARY_BENEFICIARY) ||
          roles.includes(Contact.ACTOR_INSURANCE_BENEFICIARY);
        this.actsToDisplay = this.contactService.getContactActsToShow(roles);
      }

      if (data.editPersonalSpouse) {
        this.editPersonalSpouse = data.editPersonalSpouse;
      }
      if (data.editWillExecutor) {
        this.editWillExecutor = data.editWillExecutor;
      }
      if (data.editTrustee) {
        this.editTrustee = data.editTrustee;
      }
      if (data.editBeneficiary) {
        this.editBeneficiary = data.editBeneficiary;
      }
      if (data.isInSignUp) {
        this.isInSignUp = data.isInSignUp;
      }

      if (data.type) {
        if (data.type === Contact.TYPE_DESIGNEE) {
          this.onlyaccesstype = true;
        }
      }

      if (data.openedAsFormInput) {
        this.openedAsFormInput = data.openedAsFormInput;
      }
    }
    this.editing = false;
    this.saving = false;
  }

  populateCardMinimumDataForAdmin() {
    this.apiService.wills = this.apiService.cardsMinimumData.wills;
    this.apiService.retirementAccounts =
      this.apiService.cardsMinimumData.retirementAccounts;
    this.apiService.trusts = this.apiService.cardsMinimumData.trusts;
    this.apiService.insurances = this.apiService.cardsMinimumData.insurances;
  }

  ngOnInit(): void {
    this.dialogConfig = new MatDialogConfig();
    this.buildRelatioshipOptions();
    this.buildTypeOptions();
    this.configureCardsData();
    this.setConditionalValidations();
    this.configureModelData();
  }

  ngOnDestroy(): void {
    this.typeFieldValueChangeSubscription?.unsubscribe();
  }

  setConditionalValidations() {
    this.typeFieldValueChangeSubscription =
      this.form.controls.type.valueChanges.subscribe((contactType) => {
        this.form.controls.email.clearValidators();
        this.form.controls.phone.clearValidators();

        let emailFieldValidators = [Validators.minLength(4), Validators.email];
        let phoneFieldValidators = [
          Validators.minLength(10),
          Validators.maxLength(10),
        ];
        if (contactType !== Contact.TYPE_NONE) {
          emailFieldValidators = [Validators.required, ...emailFieldValidators];
          phoneFieldValidators = [Validators.required, ...phoneFieldValidators];
        } else {
          if (this.form.controls.notifyOnDeath.value) {
            emailFieldValidators = [
              Validators.required,
              ...emailFieldValidators,
            ];
          }
        }
        this.form.controls.email.setValidators(emailFieldValidators);
        this.form.controls.phone.setValidators(phoneFieldValidators);

        this.form.controls.email.updateValueAndValidity();
        this.form.controls.phone.updateValueAndValidity();
      });

    this.typeFieldValueChangeSubscription.add(
      this.form.controls.notifyOnDeath.valueChanges.subscribe(
        (notifyOnDeath) => {
          if (this.form.controls.type.value !== Contact.TYPE_NONE) {
            return;
          }

          this.form.controls.email.clearValidators();

          let emailFieldValidators = [
            Validators.minLength(4),
            Validators.email,
          ];
          if (notifyOnDeath) {
            emailFieldValidators = [
              Validators.required,
              ...emailFieldValidators,
            ];
          }
          this.form.controls.email.setValidators(emailFieldValidators);

          this.form.controls.email.updateValueAndValidity();
        }
      )
    );
  }

  configureCardsData() {
    let self = this;
    let apiService = self.apiService;
    let apiServiceMinimumCardData = self.apiService.cardsMinimumData;
    let isVaultOpen = self.router.url.includes('vault-open');
    self.bankAccounts = isVaultOpen
      ? apiService.banks
      : apiServiceMinimumCardData.banks;
    self.realEstates = isVaultOpen
      ? apiService.realEstates
      : apiServiceMinimumCardData.realEstates;
    self.loginAccounts = isVaultOpen
      ? apiService.loginAccounts
      : apiServiceMinimumCardData.loginAccounts;
    self.trust = isVaultOpen
      ? apiService.trusts
      : apiServiceMinimumCardData.trusts;
    self.insurances = isVaultOpen
      ? apiService.insurances
      : apiServiceMinimumCardData.insurances;
    self.investments = isVaultOpen
      ? apiService.privateInvestments
      : apiServiceMinimumCardData.privateInvestments;
    self.multimedias = isVaultOpen
      ? apiService.multimedias
      : apiServiceMinimumCardData.multimedias;
    self.additionalDocs = isVaultOpen
      ? apiService.additionalDocs
      : apiServiceMinimumCardData.additionalDocs;
    self.wills = isVaultOpen
      ? apiService.wills
      : apiServiceMinimumCardData.wills;
    self.retirementAccounts = isVaultOpen
      ? apiService.retirementAccounts
      : apiServiceMinimumCardData.retirementAccounts;
    self.taxAccountants = isVaultOpen
      ? apiService.taxAccountants
      : apiServiceMinimumCardData.taxAccountants;
    self.taxReturn = isVaultOpen
      ? apiService.taxReturn
      : apiServiceMinimumCardData.taxReturn;
    self.taxDocumentation = isVaultOpen
      ? apiService.taxDocumentation
      : apiServiceMinimumCardData.taxDocumentation;
    self.taxBasis = isVaultOpen
      ? apiService.taxBasis
      : apiServiceMinimumCardData.taxBasis;
    self.identification = isVaultOpen
      ? apiService.identification
      : apiServiceMinimumCardData.identification;
  }

  configureModelData() {
    if (this.model) {
      this.form.patchValue(this.model);
      this.addModal = false;
      if (this.model.address) {
        this.address = this.model.address;
      }
      this.loadAddress();
      if (this.model.phoneChangeRequested) {
        this.phoneChangeRequested = true;
      }
    } else {
      this.editing = false;
      this.addModal = true;
      this.patchValues();
    }
    this.isAddressNewlyAdded = false;
    this.createCardsPermissionsForModel();
  }

  patchValues() {
    let valueToPatch: typeof this.form.value = {};
    if (this.editPersonalSpouse) {
      valueToPatch = {
        relationship: Contact.RELATION_SPOUSE,
        type: Contact.TYPE_NONE,
      };
    } else if (
      this.editWillExecutor ||
      this.editBeneficiary ||
      this.editTrustee
    ) {
      valueToPatch = {
        type: Contact.TYPE_NONE,
      };
    } else {
      valueToPatch = {
        type: Contact.TYPE_DESIGNEE,
      };
    }

    if (valueToPatch) {
      this.form.patchValue(valueToPatch);
    }
  }

  loadAddress() {
    const self = this;
    if (
      this.model &&
      !this.model.address &&
      this.model.addressId &&
      this.model.addressId > 0
    ) {
      const getAddressParams = { addressId: this.model.addressId };
      this.contactAddressService.getAddress(getAddressParams, {
        getResult(result) {
          if (self.model) {
            self.model.address = result.data;
          }
          self.address = result.data;
        },
        getError(_err) {
          self.errorManager.showError(
            self.errorManager.ERRORS.cant_load_address,
            MessageTitle.ERROR
          );
        },
      });
    }
  }

  createCardsPermissionsForModel() {
    let self = this;
    self.cardsPermissionsForModel = {
      bankAccounts: [],
      realEstates: [],
      loginAccounts: [],
      trust: [],
      additionalDocs: [],
      insurances: [],
      investments: [],
      multimedias: [],
      wills: [],
      retirementAccounts: [],
      taxAccountants: [],
      taxReturn: [],
      taxDocumentation: [],
      taxBasis: [],
      identification: [],
    };
    if (self.model) {
      self.insertCardIdIntoPermissionList(
        self.model.id,
        self.bankAccounts,
        self.cardsPermissionsForModel.bankAccounts
      );
      self.insertCardIdIntoPermissionList(
        self.model.id,
        self.realEstates,
        self.cardsPermissionsForModel.realEstates
      );
      self.insertCardIdIntoPermissionList(
        self.model.id,
        self.loginAccounts,
        self.cardsPermissionsForModel.loginAccounts
      );
      self.insertCardIdIntoPermissionList(
        self.model.id,
        self.trust,
        self.cardsPermissionsForModel.trust
      );
      self.insertCardIdIntoPermissionList(
        self.model.id,
        self.insurances,
        self.cardsPermissionsForModel.insurances
      );
      self.insertCardIdIntoPermissionList(
        self.model.id,
        self.investments,
        self.cardsPermissionsForModel.investments
      );
      self.insertCardIdIntoPermissionList(
        self.model.id,
        self.multimedias,
        self.cardsPermissionsForModel.multimedias
      );
      self.insertCardIdIntoPermissionList(
        self.model.id,
        self.additionalDocs,
        self.cardsPermissionsForModel.additionalDocs
      );
      self.insertCardIdIntoPermissionList(
        self.model.id,
        self.wills,
        self.cardsPermissionsForModel.wills
      );
      self.insertCardIdIntoPermissionList(
        self.model.id,
        self.retirementAccounts,
        self.cardsPermissionsForModel.retirementAccounts
      );
      self.insertCardIdIntoPermissionList(
        self.model.id,
        self.taxAccountants,
        self.cardsPermissionsForModel.taxAccountants
      );
      self.insertCardIdIntoPermissionList(
        self.model.id,
        self.taxReturn,
        self.cardsPermissionsForModel.taxReturn
      );
      self.insertCardIdIntoPermissionList(
        self.model.id,
        self.taxDocumentation,
        self.cardsPermissionsForModel.taxDocumentation
      );
      self.insertCardIdIntoPermissionList(
        self.model.id,
        self.taxBasis,
        self.cardsPermissionsForModel.taxBasis
      );
      self.insertCardIdIntoPermissionList(
        self.model.id,
        self.identification,
        self.cardsPermissionsForModel.identification
      );
    }
  }

  insertCardIdIntoPermissionList(
    contactId: number,
    cardsList: any[],
    permissionList: number[]
  ) {
    cardsList.forEach((cardData) => {
      if (cardData.permissionList.indexOf(contactId) >= 0) {
        permissionList.push(cardData.id);
      }
    });
  }

  buildRelatioshipOptions() {
    this.relationshipOptions = contactRelationships;

    if (this.editPersonalSpouse) {
      this.relationshipOptions = spouseRelationships;
    }
  }

  buildTypeOptions() {
    if (
      this.editPersonalSpouse ||
      this.editWillExecutor ||
      this.editTrustee ||
      this.editBeneficiary ||
      this.model
    ) {
      this.typeOptions = [
        { id: Contact.TYPE_NONE, name: 'None' },
        ...contactTypes,
      ];
    } else {
      this.typeOptions = contactTypes;
    }
  }

  checkAndSetClass(e) {
    let type = e.type;
    let target = e.target;
    let value = target.value;
    let parentElement = target.parentElement;
    if (value || type === 'focus') {
      parentElement.classList.add('inputted');
    } else {
      parentElement.classList.remove('inputted');
    }
  }

  closeDialog() {
    if (this.isAddressNewlyAdded) {
      this.deleteAddress(true);
    }
    this.dialogRef.close();
  }

  edit() {
    this.editing = true;
  }

  async submit(countryPhoneCode) {
    const self = this;
    this.form.markAllAsTouched();
    const params = this.form.value;
    params.countryPhoneCode = countryPhoneCode;
    params.permissionsListToAdd = self.cardsPermissionsForModel;
    if (self.form.valid) {
      var myEmail = this.form.value.email;
      if (myEmail) {
        this.form.value.email = myEmail.toLowerCase();
      }
      if (!self.model) {
        self.loading = true;
        if (Contact.RELATION_SPOUSE === params.relationship) {
          self.apiService.addContactSpouse(params, self.vaultUserForAdmin?.id, {
            getResult(response) {
              self.loading = false;
              self.addContactIdInCardsPermissionList(
                response.data.id,
                self.cardsPermissionsForModel
              );

              self.contactService.addToCachedContacts(response.data);
              self.errorManager.showSuccess(
                self.errorManager.ERRORS.add_success,
                MessageTitle.SUCCESS
              );
              self.onSaveEvent.emit({
                model: response.data,
                action: SaveEventAction.add,
                data: response,
              });
              self.model = response.data;
              self.configureModelData();
              if (self.openedAsFormInput) {
                self.closeDialog();
              }
            },
            getError(err) {
              self.loading = false;
              self.handleContactAddEditError(err);
            },
          });
        } else {
          self.apiService.addContact(params, self.vaultUserForAdmin?.id, {
            getResult(response) {
              self.loading = false;
              self.addContactIdInCardsPermissionList(
                response.data.id,
                self.cardsPermissionsForModel
              );

              self.contactService.addToCachedContacts(response.data);
              self.errorManager.showSuccess(
                self.errorManager.ERRORS.add_success,
                MessageTitle.SUCCESS
              );
              self.onSaveEvent.emit({
                model: response.data,
                action: SaveEventAction.add,
                data: response,
              });
              self.model = response.data;
              self.configureModelData();
              if (self.openedAsFormInput) {
                self.closeDialog();
              }
            },
            getError(err) {
              self.loading = false;
              self.handleContactAddEditError(err);
            },
          });
        }
      } else {
        self.loading = true;
        params.id = self.model.id;
        self.apiService.editContact(params, self.vaultUserForAdmin?.id, {
          getResult(response) {
            self.loading = false;
            self.addContactIdInCardsPermissionList(
              self.model.id,
              self.cardsPermissionsForModel
            );
            if (
              self.model.type !== params.type &&
              [Contact.TYPE_DESIGNEE].includes(params.type)
            ) {
              params.status = Contact.STATUS_INACTIVE;
            }

            self.errorManager.showSuccess(
              self.errorManager.ERRORS.details_update_success,
              MessageTitle.SUCCESS
            );
            self.saving = false;
            self.editing = false;
            for (var key in params) {
              self.model[key] = params[key];
            }
            self.contactService.editIntoCachedContacts(self.model);
            self.onSaveEvent.emit({
              model: self.model,
              action: SaveEventAction.edit,
              data: params,
            });
            self.model.phoneChangeRequested =
              self.model.phoneChangeRequested ||
              response.data.changeRequestCreated;
            self.configureModelData();
            if (self.openedAsFormInput) {
              self.closeDialog();
            }
          },
          getError(err) {
            self.loading = false;
            self.saving = false;
            self.handleContactAddEditError(err);
          },
        });
      }
    }
  }

  handleContactAddEditError(err) {
    if (err.error.error === this.errorManager.ERRORS.connection_exists) {
      let errorMessage =
        'Email is already being used by another contact of yours.';
      const existingContact = err.error.data;
      if (existingContact) {
        errorMessage = `Email is already being used by "${existingContact.first_name} ${existingContact.last_name}" 
          .  Please select them from the list of existing contacts.`;
      }

      this.errorManager.showError(errorMessage, MessageTitle.ERROR);
    } else {
      this.errorManager.showError(
        this.errorManager.getMessage(err.error),
        MessageTitle.ERROR
      );
    }
  }

  addContactIdInCardsPermissionList(
    contactId: number,
    newCardsPermissionList: any
  ) {
    let self = this;
    for (var prop in newCardsPermissionList) {
      if (Object.prototype.hasOwnProperty.call(newCardsPermissionList, prop)) {
        self[prop].forEach((data) => {
          if (
            data.permissionList &&
            data.permissionList.indexOf(contactId) >= 0
          ) {
            data.permissionList.splice(
              data.permissionList.indexOf(contactId),
              1
            );
          }

          if (newCardsPermissionList[prop].indexOf(data.id) >= 0) {
            var permissionList = [];
            if (data.permissionList) {
              permissionList = data.permissionList;
            }
            permissionList.push(contactId);
            data.permissionList = permissionList;
          }
        });
      }
    }
  }

  saveContact() {
    let self = this;
    if (self.isInSignUp) {
      self.form.markAllAsTouched();
      var myEmail = self.form.value.email;
      if (myEmail) {
        self.form.value.email = myEmail.toLowerCase();
      }
      let params = self.form.value;
      params.countryPhoneCode = self.userService.user.countryPhoneCode;
      params.permissionsListToAdd = self.cardsPermissionsForModel;
      if (self.form.valid) {
        self.onSaveEvent.emit({
          model: params,
          action: SaveEventAction.add,
          data: params,
        });
        if (self.openedAsFormInput) {
          self.closeDialog();
        }
      }
    } else {
      self.validatePhoneNumberAndcreateUser();
    }
  }

  async validatePhoneNumberAndcreateUser() {
    const self = this;
    const { phone, type } = this.form.value;
    let countryPhoneCode = self.userService.user.countryPhoneCode;
    if (this.usertype === UserType.ADMIN) {
      countryPhoneCode = this.vaultUserForAdmin.countryPhoneCode;
    }

    const params = {
      phoneNumber: phone,
      countryPhoneCode: countryPhoneCode,
    };
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    if (type === Contact.TYPE_NONE && !phone) {
      this.form.markAllAsTouched();
      self.submit(countryPhoneCode);
      return;
    }

    this.userService.validatePhoneNumber(params, {
      getResult(_result) {
        self.submit(countryPhoneCode);
      },
      getError(err) {
        self.errorManager.showError(
          self.errorManager.ERRORS[err.error],
          MessageTitle.ERROR
        );
        return;
      },
    });
  }

  addAddress() {
    const self = this;
    const breadcrumbs =
      this.data && this.data.breadcrumbs
        ? `${this.data.breadcrumbs} > Add Contact`
        : 'Add Contact';
    self.hide = true;
    self.dialogConfig = new MatDialogConfig();
    self.dialogConfig.data = {
      breadcrumbs,
      vaultUserForAdmin: this.vaultUserForAdmin,
    };
    let dialogRef2 = self.matDialog.open(
      AddContactAddressComponent,
      self.dialogConfig
    );
    dialogRef2.afterClosed().subscribe((value) => {
      self.hide = false;
      if (value) {
        if (value.add) {
          let dataToAdd = value.data;
          self.address = dataToAdd;
          self.isAddressNewlyAdded = true;
          if (self.model) {
            self.model.address = self.address;
          }
          self.form.patchValue({ addressId: dataToAdd.addressId });
        }
      }
    });
  }

  editAddress() {
    var self = this;
    self.hide = true;
    self.dialogConfig = new MatDialogConfig();
    self.dialogConfig.data = {
      model: self.address,
      edit: true,
      vaultUserForAdmin: this.vaultUserForAdmin,
    };
    let dialogRef2 = self.matDialog.open(
      AddContactAddressComponent,
      self.dialogConfig
    );
    dialogRef2.afterClosed().subscribe((value) => {
      self.hide = false;
      if (value) {
        if (value.edit) {
          let dataToAdd = value.data;
          self.address = dataToAdd;
          if (self.model) {
            self.model.address = self.address;
          }
          self.form.patchValue({ addressId: dataToAdd.addressId });
        } else {
          if (value.delete) {
            self.address = null;
            self.isAddressNewlyAdded = false;
            if (self.model) {
              self.model.address = null;
              const cachedContact = self.contactService.getContactById(
                self.model.id
              );
              if (cachedContact && cachedContact.address !== null) {
                cachedContact.address = null;
              }
            }
            self.form.patchValue({ addressId: 0 });
          }
        }
      }
    });
  }

  destroy() {
    const self = this;
    this.getDeletionConfirmation().subscribe((destroy) => {
      if (destroy) {
        self.loading = true;
        var deleteAddressId = 0;
        if (self.address) {
          deleteAddressId = self.address.addressId;
        }
        var params = {
          id: self.model.id,
          addressId: deleteAddressId,
        };
        if (Contact.RELATION_SPOUSE === self.model.relationship) {
          this.apiService.deleteContactSpouse(
            params,
            self.vaultUserForAdmin?.id,
            {
              getResult(_response) {
                self.loading = false;
                self.contactService.deleteFromCachedContacts(self.model);
                self.errorManager.showSuccess(
                  self.errorManager.ERRORS.delete_success,
                  MessageTitle.SUCCESS
                );
                self.onSaveEvent.emit({
                  model: self.model,
                  action: SaveEventAction.delete,
                  data: params,
                });
                self.closeDialog();
              },
              getError(err) {
                self.loading = false;
                self.errorManager.showError(
                  self.errorManager.ERRORS[err.error],
                  MessageTitle.ERROR
                );
              },
            }
          );
        } else {
          this.apiService.deleteContact(params, self.vaultUserForAdmin?.id, {
            getResult(_response) {
              self.loading = false;
              self.contactService.deleteFromCachedContacts(self.model);
              self.errorManager.showSuccess(
                self.errorManager.ERRORS.delete_success,
                MessageTitle.SUCCESS
              );
              self.onSaveEvent.emit({
                model: self.model,
                action: SaveEventAction.delete,
                data: params,
              });
              self.closeDialog();
            },
            getError(err) {
              self.loading = false;
              self.errorManager.showError(
                self.errorManager.ERRORS[err.error],
                MessageTitle.ERROR
              );
            },
          });
        }
      }
    });
  }

  getDeletionConfirmation() {
    let message = `You are about to permanently delete ${this.model.first_name} ${this.model.last_name}.`;
    if (this.actsToDisplay?.length > 0) {
      message += `<br>This person is acting as: ${this.actsToDisplay}.`;
    }

    if (this.model.type !== Contact.TYPE_NONE) {
      let typeOption = this.typeOptions.find(
        (type) => type.id === this.model.type
      );
      if (typeOption) {
        message += `<br>This person also has role of ${typeOption.name}.`;
      }
    }

    const dialogRef = this.matDialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: {
        title: 'Are you sure?',
        message: message,
      },
    });

    return dialogRef.afterClosed();
  }

  deleteAddressWithConfirmation() {
    if (this.address) {
      let dialogRef = this.matDialog.open(ConfirmDialogComponent, {
        disableClose: true,
        data: {
          title: 'Are you sure?',
          message: "You are about to delete this contact's address",
        },
      });

      dialogRef.afterClosed().subscribe((destroy) => {
        if (destroy) {
          this.deleteAddress(false);
        }
      });
    }
  }

  private deleteAddress(silent) {
    if (!this.address) {
      return;
    }
    this.loading = true;
    const self = this;
    this.contactAddressService.deleteAddress(this.address, {
      getResult(_response) {
        self.address = null;
        self.isAddressNewlyAdded = false;
        if (self.model) {
          if (self.model.address) {
            self.model.address = null;
            const cachedContact = self.contactService.getContactById(
              self.model.id
            );
            if (cachedContact && cachedContact.address !== null) {
              cachedContact.address = null;
            }
          }
        }
        self.form.patchValue({ addressId: 0 });
        if (!silent) {
          self.errorManager.showSuccess(
            self.errorManager.ERRORS.delete_success,
            MessageTitle.SUCCESS
          );
        }
        self.loading = false;
      },
      getError(err) {
        self.loading = false;
        if (!silent) {
          self.errorManager.showError(
            self.errorManager.getMessage(err.error),
            MessageTitle.ERROR
          );
        }
      },
    });
  }

  resendInvitation() {
    const self = this;
    const params = {
      contactId: self.model.id,
    };
    self.loading = true;
    self.apiService.resendContactInvitation(
      params,
      self.vaultUserForAdmin?.id,
      {
        getResult(_result) {
          self.errorManager.showSuccess(
            self.errorManager.ERRORS.resend_contact_invitation_success,
            MessageTitle.SUCCESS
          );
          self.loading = false;
        },
        getError(err) {
          self.loading = false;
          if (
            typeof err.error === 'string' &&
            self.errorManager.ERRORS[err.error]
          ) {
            self.errorManager.showError(
              self.errorManager.getMessage(err.error),
              MessageTitle.ERROR
            );
          } else {
            self.errorManager.showError(
              self.errorManager.ERRORS.resend_contact_invitation_fail,
              MessageTitle.ERROR
            );
          }
        },
      }
    );
  }
}
